import React, { createContext, useEffect, useMemo, useState } from "react";
import { LocaleHelper, LocaleManager } from "@bryntum/core-thin";
import { useI18nContext } from "@procore/core-react";

import ganttEn from "@bryntum/gantt/locales/gantt.locale.En.js";
import ganttEnGb from "@bryntum/gantt/locales/gantt.locale.EnGb.js";
import ganttDe from "@bryntum/gantt/locales/gantt.locale.De.js";
import ganttFrFr from "@bryntum/gantt/locales/gantt.locale.FrFr.js";
import ganttEs from "@bryntum/gantt/locales/gantt.locale.Es.js";
import ganttIt from "@bryntum/gantt/locales/gantt.locale.It.js";
import ganttJa from "@bryntum/gantt/locales/gantt.locale.Ja.js";
import ganttPl from "@bryntum/gantt/locales/gantt.locale.Pl.js";
import ganttPtBr from "@bryntum/gantt/locales/gantt.locale.PtBr.js";
import ganttTh from "@bryntum/gantt/locales/gantt.locale.Th.js";
import ganttZhCn from "@bryntum/gantt/locales/gantt.locale.ZhCn.js";
import ganttZhTw from "@bryntum/gantt/locales/gantt.locale.ZhTw.js";
import ganttEnAu from "../components/gantt/locales/gantt.locale.EnAu.js";

type BryntumLocaleContextType = {
   locale: string;
};

export const BryntumLocaleContext = createContext<BryntumLocaleContextType>({ locale: "en-US" });

const ganttLocaleMap: Record<string, any> = {
   en: ganttEn,
   "en-US": ganttEn,
   "en-AU": ganttEnAu,
   "en-CA": ganttEn,
   "en-GB": ganttEnGb,
   es: ganttEs,
   "es-ES": ganttEs,
   "fr-CA": ganttFrFr,
   "fr-FR": ganttFrFr,
   "de-DE": ganttDe,
   "it-IT": ganttIt,
   "ja-JP": ganttJa,
   "pl-PL": ganttPl,
   "pt-BR": ganttPtBr,
   "th-TH": ganttTh,
   "zh-SG": ganttZhCn,
   "zh-TW": ganttZhTw,
   "is-IS": ganttEn,
};

export const BryntumLocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
   const I18n = useI18nContext();
   const [locale, setLocale] = useState("en-US");

   useEffect(() => {
      function applyLocale(code: string) {
         const localeModule = ganttLocaleMap[code] || ganttLocaleMap["en"];
         try {
            LocaleHelper.publishLocale(code, localeModule);
            LocaleManager.locale = code;
            setLocale(code);
         } catch (e) {
            LocaleHelper.publishLocale("en", ganttLocaleMap["en"]);
            LocaleManager.locale = "en";
            setLocale("en");
         }
      }
      if (I18n.locale) {
         applyLocale(I18n.locale);
      }
   }, [I18n.locale]);

   const memoizedValue = useMemo(() => ({ locale }), [locale]);

   return (
      <BryntumLocaleContext.Provider value={memoizedValue}>
         {children}
      </BryntumLocaleContext.Provider>
   );
};
